import { parseDateUtc } from '@/helpers/parseToUTC';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useColorModeValue,
} from '@chakra-ui/react';
import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { safeDynamic } from '@/utils/safeDynamic';
import 'react-datepicker/dist/react-datepicker.css';

const areSameDates = (date1: Date | null, date2: Date | null): boolean => {
  if (!date1 || !date2) return false;
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

export interface SingleDatePickFieldProps {
  id?: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  name: string;
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rightElement?: React.ReactNode;
  fontSize?: any;
  fontWeight?: string;
  italic?: boolean;
  isDisabled?: boolean;
  labelFontSize?: string;
  mb?: string | number;
  value?: string;
  helpers?: any;
  setFieldValue?: (value: string) => void;
  setTouched?: (touched: boolean) => void;
  h?: string;
  bg?: string;
  borderColor?: string;
  border?: string;
  labelMb?: number | string;
  [x: string]: any;
}

const DatePicker = safeDynamic<any>(() => import('react-datepicker'), {
  ssr: false,
});

export function SingleDatePickField(props: SingleDatePickFieldProps) {
  const {
    id,
    label,
    placeholder = 'Select date',
    mb,
    isRequired,
    name,
    errors,
    touched,
    fontSize,
    fontWeight,
    italic,
    value,
    helpers,
    setFieldValue,
    isDisabled,
    labelFontSize,
    setTouched,
    h,
    bg,
    borderColor,
    border,
    labelMb = 4,
    onDark,
    ...rest
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const [selectedDate, setSelectedDate] = useState<Date | null>(parseDateUtc(value));
  const userInteractionRef = useRef(false);
  const preventEffectRef = useRef(false);

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return placeholder;

    try {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });

      return formatter.format(date);
    } catch (error) {
      console.warn('Failed to format display date:', date, error);
      return placeholder;
    }
  };

  useEffect(() => {
    if (preventEffectRef.current) {
      preventEffectRef.current = false;
      return;
    }

    if (!userInteractionRef.current) {
      const newDate = parseDateUtc(value);
      if (!areSameDates(newDate, selectedDate)) {
        setSelectedDate(newDate);
      }
    }
  }, [value, selectedDate]);

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    userInteractionRef.current = true;
    preventEffectRef.current = true;

    try {
      setSelectedDate(date);
      const formattedDate = formatDisplayDate(date);

      if (setFieldValue) {
        setFieldValue(formattedDate);
      } else if (helpers?.setValue) {
        helpers.setValue(formattedDate);
      }
    } finally {
      userInteractionRef.current = false;
    }
  };

  const handleCalendarOpen = () => {
    userInteractionRef.current = true;

    if (setTouched) {
      setTouched(true);
    } else if (helpers?.setTouched) {
      helpers.setTouched(true);
    }
  };

  const displayDate = formatDisplayDate(selectedDate);

  const CustomInput = forwardRef<HTMLDivElement, any>(({ onClick, onFocus, onBlur }, ref) => (
    <Flex
      ref={ref}
      w="100%"
      border={border || '2px solid'}
      borderColor={borderColor || (isDisabled ? 'gray' : 'gray.600')}
      borderRadius="10px"
      bg={bg || 'transparent'}
      h={h || '60px'}
      maxH="60px"
      onClick={isDisabled ? undefined : onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      align="center"
      px="15px"
      pl={{ base: '15px', md: '30px' }}
      pr={{ base: '15px', md: '30px' }}
      tabIndex={isDisabled ? -1 : 0}
      role="button"
      aria-label={label ? `${label} date picker` : 'Date picker'}
      _focus={{
        outline: 'none',
        borderColor: 'blue.500',
        boxShadow: '0 0 0 1px #3182ce',
      }}
    >
      <Box
        as="span"
        color={isDisabled ? 'gray' : textColorPrimary}
        fontSize={fontSize || '20px'}
        fontWeight={fontWeight || '500'}
        style={{ fontStyle: italic ? 'italic' : 'normal' }}
        whiteSpace="nowrap"
        flex="1"
        textAlign="left"
        opacity={selectedDate ? 1 : 0.7}
        pointerEvents="none"
      >
        {displayDate}
      </Box>
    </Flex>
  ));

  return (
    <Flex direction="column" mb={mb ?? '30px'} width="full" pos="relative">
      <FormControl
        isRequired={isRequired}
        isInvalid={!!errors && touched}
        isDisabled={isDisabled}
        width="100%"
      >
        {label && (
          <FormLabel
            display="flex"
            htmlFor={id}
            fontSize={labelFontSize || '2xl'}
            lineHeight="normal"
            color={textColorPrimary}
            fontWeight="bold"
            mb={labelMb}
            _hover={{ cursor: 'pointer' }}
          >
            {label}
          </FormLabel>
        )}
        <Box w="100%">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            onCalendarOpen={handleCalendarOpen}
            disabled={isDisabled}
            dateFormat="dd MMMM yyyy"
            customInput={<CustomInput />}
          />
        </Box>
        {errors && touched && <FormErrorMessage ml="10px">{errors}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
